import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Permission } from '../../../../Generated/Raven-Demeter';
import LinkButton, { LinkButtonType } from '../../../Components/Form/Buttons/LinkButton';
import Switch from '../../../Components/Form/Buttons/Switch';
import ComponentHeader from '../../../Components/Headers/ComponentHeader';
import useSearchParameters from '../../../Components/Navigation/Hooks/useSearchParametersHook';
import useLanguage from '../../../Services/Language/useLanguageHook';
import PermissionOptionsDropdown from './PermissionOptionsDropdown';
import styles from './Permissions.module.scss';
import { PermissionDefinition, SubPermissionDefinition } from './PermissionsDefinitions';
import SubPermissionCheckbox from './SubPermissionCheckbox';

export interface IPermissionsRawProps {
    permissionDefinitions: PermissionDefinition[];
    handlePermissionsChange: (permissions: Permission[]) => void;
}

const PermissionsRaw: React.FC<IPermissionsRawProps> = (props: IPermissionsRawProps) => {
    const [translations] = useLanguage();
    const [, setSearchParameters] = useSearchParameters();
    const navigate = useNavigate();

    // State hooks.
    const [savedPermissions, setSavedPermissions] = useState<PermissionDefinition[]>([]);

    useEffect(() => {
        // If we get a new permission set, we will reset everything to that permission set.
        setSavedPermissions(props.permissionDefinitions);
    }, [props.permissionDefinitions]);

    const handleSimplePermissionChange = (permission: PermissionDefinition, value: boolean) => {
        const index = savedPermissions.findIndex((x) => x.permissionType === permission.permissionType);
        const matchingPermission: PermissionDefinition = {
            ...savedPermissions[index],
            hasPermission: !!value,
        };

        const newSavedPermissions = [...savedPermissions];
        newSavedPermissions[index] = matchingPermission;
        setSavedPermissions(newSavedPermissions);
    };

    const handleCheckboxPermissionChange = (permission: PermissionDefinition, subPermissions: SubPermissionDefinition[]) => {
        const index = savedPermissions.findIndex((x) => x.permissionType === permission.permissionType);
        const matchingPermission: PermissionDefinition = {
            ...savedPermissions[index],
            hasPermission: subPermissions.some((x) => x.hasPermission),
            subPermissions,
        };

        const newSavedPermissions = [...savedPermissions];
        newSavedPermissions[index] = matchingPermission;
        setSavedPermissions(newSavedPermissions);
    };

    const handleOptionPermissionChange = (permission: PermissionDefinition, permissionOption: string) => {
        const index = savedPermissions.findIndex((x) => x.permissionType === permission.permissionType);
        const matchingPermission: PermissionDefinition = {
            ...savedPermissions[index],
            hasPermission: true, // If there are options, the permission is always true.
            subPermissions: [
                {
                    title: permissionOption,
                    key: permissionOption,
                    hasPermission: true,
                },
            ],
        };

        if (permission.handlePermissionOptionChange) {
            permission.handlePermissionOptionChange(matchingPermission, permissionOption);
        }

        const newSavedPermissions = [...savedPermissions];
        newSavedPermissions[index] = matchingPermission;
        setSavedPermissions(newSavedPermissions);
    };

    const handleSave = () => {
        props.handlePermissionsChange(
            savedPermissions.map((permission) => {
                const mappedPermission: Permission = {
                    permissionType: permission.permissionType,
                    hasPermission: permission.hasPermission,
                };

                if (permission.subPermissions && permission.subPermissions?.length > 0) {
                    const subPermissions: { [key: string]: boolean } = {};

                    permission.subPermissions.forEach((x) => {
                        subPermissions[x.key] = x.hasPermission;
                    });

                    mappedPermission.subPermissions = subPermissions;
                }

                return mappedPermission;
            }),
        );
    };

    const navigateBackToCompanies = () => {
        setSearchParameters({ tab: 'Companies' });
        navigate(-1);
    };

    return (
        <>
            <ComponentHeader title={translations.words.permissions} />
            {savedPermissions.length > 0 &&
                savedPermissions
                    .filter((permission) => permission.isVisible)
                    .map((permission) => (
                        <React.Fragment key={permission.permissionType}>
                            {!permission.subPermissions && !permission.getPermissionOptions && (
                                <div>
                                    <Switch
                                        checked={permission.hasPermission}
                                        disabled={permission.isDisabled}
                                        handleChange={(value) => handleSimplePermissionChange(permission, value)}
                                        track={{
                                            eventCategory: 'Permissions',
                                            eventAction: `Permission${permission.permissionType}Changed`,
                                        }}
                                    />
                                    {permission.titleGetter()}
                                </div>
                            )}
                            {permission.subPermissions && !permission.getPermissionOptions && (
                                <SubPermissionCheckbox
                                    permission={permission}
                                    handleSubPermissionsChange={(subPermissions) => handleCheckboxPermissionChange(permission, subPermissions)}
                                    disabled={permission.isDisabled}
                                />
                            )}
                            {permission.getPermissionOptions && (
                                <PermissionOptionsDropdown
                                    permission={permission}
                                    permissionOptions={permission.getPermissionOptions()}
                                    handlePermissionChange={(permissionOption) => handleOptionPermissionChange(permission, permissionOption)}
                                    disabled={permission.isDisabled}
                                />
                            )}
                        </React.Fragment>
                    ))}
            <div className={styles.update_permission_submit_action_row}>
                <LinkButton
                    title={translations.actions.cancel}
                    onClick={navigateBackToCompanies}
                    type={LinkButtonType.White}
                    track={{ eventCategory: 'Permissions', eventAction: 'CancelClicked' }}
                />

                <LinkButton
                    title={`${translations.actions.save} ${translations.words.permissions}`}
                    type={LinkButtonType.Blue}
                    onClick={() => {
                        handleSave();
                    }}
                    track={{ eventCategory: 'Permissions', eventAction: 'SaveClicked' }}
                />
            </div>
        </>
    );
};

export default PermissionsRaw;
